// This file exists because of circular dependency errors
// https://ngrx-entity-relationship.sudo.eu/help/circular-dependency

import { createSelector } from '@ngrx/store';
import { selectFocusedLocation, selectFocusedLocationId, selectLocations, selectOngoingActions } from '../../location-list/state/location-list-selectors';
import { CCMatrixRequestPayload } from '../../../../models/locations-models/cc-matrix-payload';
import { selectCcDataTimestamp, selectCCListPossiblyOutOfDate, selectCcLocation, selectCcLocationGroup, selectCcLocationsArray, selectCcLocationsCount, selectCcLocationsDictionary, selectCcLocationsLoading, selectCcRowSettingsDictionary, selectContinueAfterClaim, selectContinueAfterCreate, selectHoveredCcRowSetting, selectSelectedCcRows } from './cc-list-selectors';
import { selectIsCvOn } from '../../state/location-skeleton-combined-selectors';

/**
 * Data for CC Table
 */
export const selectCCTableData = createSelector({
  ccLocations: selectCcLocationsArray,
  hoveredCcRowSetting: selectHoveredCcRowSetting,
  ccRowSettingsDictionary: selectCcRowSettingsDictionary,
  focusedLocation: selectFocusedLocation,
  focusedLocationId: selectFocusedLocationId,
  ccListPossiblyOutOfDate: selectCCListPossiblyOutOfDate,
  ongoingActions: selectOngoingActions,
  timestamp: selectCcDataTimestamp,
  continueAfterCreate: selectContinueAfterCreate,
  continueAfterClaim: selectContinueAfterClaim,
});

export const selectCcMenuRowViewModel = createSelector({
  focusedLocationId: selectFocusedLocationId,
  ccRowSettingsDictionary: selectCcRowSettingsDictionary,
  ccLocationGroup: selectCcLocationGroup,
  ccLocationsLoading: selectCcLocationsLoading,
  isCvOn: selectIsCvOn
});

/**
 * Request payload for getting CC Matrix
 */
export const selectCCMatrixRequestPayload = (id?: number) => createSelector(selectFocusedLocation,
    selectLocations,
    (focusedLocation, locations) => {
      const payload: CCMatrixRequestPayload = {
        focusedLocationId: id ? id : focusedLocation?.id,
        locationsOnPage: locations.map(location => {
          return {
            locationId: location.id,
            latLng: {
              lat: location.coordinate ? location.coordinate.latitude : location.coordinateImport?.latitude,
              lng: location.coordinate ? location.coordinate.longitude : location.coordinateImport?.longitude,
            },
          };
        }),
      };
      return payload;
    });

/**
 * Determines the checked-all state of cc-list's master toggle
 */
export const selectAllCcRowsSelected = createSelector(
    selectCcLocationsCount, selectSelectedCcRows, (ccLocationsCount, selectedCcRows) => {
      return ccLocationsCount && selectedCcRows.length === ccLocationsCount;
    });

/**
 * Determines the indeterminate state of cc-list's master toggle
 */
export const selectSomeCcRowsSelected = createSelector(selectCcLocationsCount,
    selectSelectedCcRows, (ccLocationsCount, selectedCcRows) => {
      return selectedCcRows.length > 0 && selectedCcRows.length !== ccLocationsCount;
    });

export const selectSelectedCcLocations =
    createSelector(selectSelectedCcRows,
        selectCcLocationsDictionary, (selectedCcRows,
                                      ccLocationsDictionary) =>
            selectedCcRows.map(ccRow => ccLocationsDictionary[ccRow.placeId]),
    );

export const selectCCRowColumnViewModel = createSelector({
  focusedLocationId: selectFocusedLocationId,
  allCcRowsSelected: selectAllCcRowsSelected,
  someCcRowsSelected: selectSomeCcRowsSelected,
  ccRowSettingsDictionary: selectCcRowSettingsDictionary,
  ccLocationsCount: selectCcLocationsCount,
});

export const selectCCActionButtonsViewModel = (placeId: string) => createSelector({
  ccMenuRowViewModel: selectCcMenuRowViewModel,
  ccLocation: selectCcLocation(placeId)
});